import api from "features/api";

/** The integration settings the backend knows; each names one secret. */
export type IntegrationKey =
  | "jaah_webhook_url"
  | "jaah_security_token"
  | "jaah_external_api_key";

/** A secret as this tab sees it: its name only, never its value. */
export interface SecretOption {
  id: number;
  name: string;
}

export interface IntegrationField {
  key: IntegrationKey;
  label: string;
  /** `null` while the field is not bound to any secret. */
  secret: SecretOption | null;
}

export interface IntegrationSettings {
  fields: IntegrationField[];
  /** Every secret, by name, whether it is encrypted or not. */
  secret_options: SecretOption[];
}

/** Only the fields being changed; `null` unbinds one. */
export type UpdateIntegrationsParams = Partial<
  Record<IntegrationKey, number | null>
>;

export const apiWithIntegrations = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrations: builder.query<IntegrationSettings, void>({
      query: () => `/integrations/`,
      providesTags: ["Integrations"],
    }),

    updateIntegrations: builder.mutation<
      IntegrationSettings,
      UpdateIntegrationsParams
    >({
      query: (body) => ({ url: `/integrations/`, method: "PATCH", body }),
      // The answer is the new state, so the picker shows the pick at once
      // instead of flicking back until the refetch lands.
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            apiWithIntegrations.util.upsertQueryData(
              "getIntegrations",
              undefined,
              data,
            ),
          );
        } catch {
          // The component reports the failure; the cache stays as it was.
        }
      },
      // Each change writes an audit row, shown in the Secrets tab.
      invalidatesTags: ["Integrations", "SecretAudit"],
    }),
  }),
});

export const { useGetIntegrationsQuery, useUpdateIntegrationsMutation } =
  apiWithIntegrations;
